'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronRight, Heart, MapPin, Video, X } from 'lucide-react';
import type { Congregation, LocationGroup, ServiceTime } from '@/lib/types';
import { campusDetailsUrl, getPrimary, getSubCongregations, servicesByDay } from '@/lib/types';
import { useCampusSelection, type CampusSelection } from '@/lib/useCampusSelection';
import { SECTION_HASHES } from '@/lib/scrollToSection';
import {
  fetchCampusAnnouncements,
  type CampusAnnouncement,
  type CampusAnnouncements,
  type CampusLive,
} from '@/lib/api';

export interface CampusPanelProps {
  apiBaseUrl?: string;
  /** Campus pages live under this URL: `${detailsBaseUrl}/${slug}/` */
  detailsBaseUrl?: string;
  /** The "View all campuses" link */
  viewAllUrl?: string;
  /** Photo behind the heading, the mega menu's own by default; empty string = no photo tile */
  imageUrl?: string;
  /** A plain click on a link in the panel is leaving the page: close whatever holds it */
  onNavigate?: () => void;
  /**
   * The panel brings its own padding, with the footer row ("Scroll for
   * more", "View all locations") filling the bottom padding exactly, so the
   * cards have the same space above and below. On for the mega menu, whose
   * theme column padding is zeroed for it (EMBED-mega-menu-glass.css); the
   * header form's popover pads itself.
   */
  padded?: boolean;
}

/** Where campus pages live; `/{slug}/` is appended */
export const DEFAULT_DETAILS_BASE_URL = 'https://woodsidebible.org/locations';

/** The photo the site's Locations mega menu already uses */
export const DEFAULT_IMAGE_URL =
  'https://woodsidebible.org/wp-content/uploads/2025/07/Sprint3_NewVisitor_FamilyOnPurpose_5-copy.jpeg';

const TEAL = '#299c8f';
const NAVY = '#1c2b39';
/**
 * The hash for "this campus page, at its announcements". It matches no id on
 * purpose, so the page opens at the top; the widget's script on that page
 * then scrolls down to the announcements smoothly (lib/scrollToSection.ts).
 */
const ANNOUNCEMENTS_HASH = SECTION_HASHES.announcements;

/** The cards column's bottom edge while there is more to scroll to */
const SCROLL_FADE: React.CSSProperties = {
  maskImage: 'linear-gradient(to bottom, #000 calc(100% - 56px), transparent)',
  WebkitMaskImage: 'linear-gradient(to bottom, #000 calc(100% - 56px), transparent)',
};

/** How long a card takes to slide to its new slot after a pick */
export const FLIP_MS = 420;

/**
 * The panel form of the campus picker: every campus laid out as cards, for
 * the site header's Locations mega menu. A card is a location; a location
 * with more than one congregation (Troy, Troy Español) shows each one with
 * its own pastor, service times, "Set as my campus" and "Campus details".
 * Picking writes the cookie now and MinistryPlatform in the background when
 * signed in.
 */
export function CampusPanel({
  apiBaseUrl = 'https://api.woodsidebible.org',
  ...rest
}: CampusPanelProps) {
  const selection = useCampusSelection(apiBaseUrl);
  return <CampusPanelView selection={selection} padded {...rest} />;
}

export interface CampusPanelViewProps extends Omit<CampusPanelProps, 'apiBaseUrl'> {
  selection: CampusSelection;
  /** Called after a heart pick, so a popover can close */
  onPicked?: () => void;
}

/** The panel with the selection passed in, for a trigger that already owns one */
export function CampusPanelView({
  selection,
  detailsBaseUrl = DEFAULT_DETAILS_BASE_URL,
  viewAllUrl = 'https://woodsidebible.org/locations/',
  imageUrl = DEFAULT_IMAGE_URL,
  onPicked,
  onNavigate,
  padded = false,
}: CampusPanelViewProps) {
  const {
    apiBaseUrl,
    locations,
    isLoading,
    error,
    selectedId,
    selectedCong,
    selectedSvgUrl,
    select,
    liveByCampus,
  } = selection;
  const scrollRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const cap = useCardsColumnCap(rootRef, columnRef);
  const more = useScrollMore(scrollRef);

  // Your campus leads the list, the way the dropdown leads with it. When
  // the pick changes, the cards slide to their new places (FLIP: measure
  // before, re-measure after, animate the difference) rather than jumping.
  const sorted = useMemo(() => {
    const byName = [...locations].sort((a, b) =>
      getPrimary(a).name.localeCompare(getPrimary(b).name)
    );
    if (!selectedId) return byName;
    const i = byName.findIndex((g) => g.congregations.some((c) => c.id === selectedId));
    if (i <= 0) return byName;
    return [byName[i], ...byName.slice(0, i), ...byName.slice(i + 1)];
  }, [locations, selectedId]);

  const cardEls = useRef(new Map<number, HTMLElement>());
  const lastRects = useRef(new Map<number, DOMRect>());
  useLayoutEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const next = new Map<number, DOMRect>();
    for (const [id, el] of cardEls.current) {
      const rect = el.getBoundingClientRect();
      next.set(id, rect);
      const prev = lastRects.current.get(id);
      if (!prev || reduce || rect.width === 0) continue;
      const dx = prev.left - rect.left;
      const dy = prev.top - rect.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
        duration: FLIP_MS,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      });
    }
    lastRects.current = next;
  }, [sorted]);
  const registerCard = useCallback((id: number, el: HTMLElement | null) => {
    if (el) cardEls.current.set(id, el);
    else cardEls.current.delete(id);
  }, []);

  // One listener for every link in the panel. Handlers that keep the page
  // (the heart, the live dot, "stream or page?") stop or prevent the click
  // before it gets here; a new tab or a modified click leaves the panel open.
  const onPanelClick = (e: React.MouseEvent) => {
    if (!onNavigate || e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = (e.target as Element).closest?.('a[href]') as HTMLAnchorElement | null;
    if (!link || (link.target && link.target !== '_self')) return;
    onNavigate();
  };

  const status = selectedCong ? (
    <>
      Your campus is <span className="font-semibold">{selectedCong.name}</span>. Pick another any
      time.
    </>
  ) : (
    'We remember it everywhere on the site.'
  );

  // With the photo layout, your campus takes the photo's place as a bigger
  // card with its announcements, and leaves the grid so it is not shown twice.
  // A location with a second congregation stays, since the other one still
  // needs a card.
  const featured = imageUrl && !isLoading && selectedCong ? selectedCong : null;
  const gridGroups = featured
    ? sorted.filter((g) => !(g.congregations.length === 1 && g.congregations[0].id === featured.id))
    : sorted;

  const cards = (
    <div className="grid grid-cols-1 gap-2.5 @[40rem]:grid-cols-2 @[72rem]:grid-cols-3">
      {isLoading &&
        Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="h-[104px] animate-pulse rounded-xl bg-gray-100" />
        ))}
      {!isLoading &&
        gridGroups.map((group) => (
          <LocationCard
            key={group.id}
            group={group}
            cardRef={(el) => registerCard(group.id, el)}
            selectedId={selectedId}
            detailsBaseUrl={detailsBaseUrl}
            liveByCampus={liveByCampus}
            onSelect={(cong, svgUrl) => {
              select(cong, svgUrl);
              onPicked?.();
            }}
          />
        ))}
    </div>
  );

  const errorLine = error && !isLoading && (
    <p className="py-6 text-center text-sm text-gray-500">
      Campuses are not loading right now.{' '}
      <a href={viewAllUrl} className="font-semibold underline" style={{ color: TEAL }}>
        See all campuses
      </a>
    </p>
  );

  // Without a photo: a plain heading row above the cards
  if (!imageUrl) {
    return (
      <div
        className={`@container w-full font-sans ${padded ? 'p-10' : ''}`}
        style={{ color: NAVY }}
        onClick={onPanelClick}
      >
        <div className="mb-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
          <div>
            <h2 className="m-0 text-[24px] leading-tight font-bold" style={{ color: NAVY }}>
              Choose your campus
            </h2>
            <p className="mt-0.5 text-[12px] text-gray-500">{status}</p>
          </div>
          <ViewAllLink href={viewAllUrl} />
        </div>
        {errorLine}
        {cards}
      </div>
    );
  }

  // With the photo: the mega menu's own layout, a photo tile on the left with
  // the heading over it, cards on the right. Narrow: the tile becomes a banner.
  return (
    <div
      ref={rootRef}
      className={`@container w-full font-sans ${padded ? 'px-10 pt-10' : ''}`}
      style={{ color: NAVY }}
      onClick={onPanelClick}
    >
      <div className="flex flex-col gap-4 @[48rem]:flex-row @[48rem]:items-stretch @[48rem]:gap-6">
        {featured ? (
          // Stretches to the cards column's height, like the photo it replaces
          <div className="flex flex-shrink-0 flex-col @[48rem]:w-[36%]">
            <FeaturedCampus
              cong={featured}
              svgUrl={selectedSvgUrl}
              href={campusDetailsUrl(featured, detailsBaseUrl)}
              live={liveByCampus.get(featured.id)}
              apiBaseUrl={apiBaseUrl}
            />
          </div>
        ) : (
          // The whole tile is the "view all" link; the label inside is just a
          // label, so there is one anchor and one focus stop.
          <a
            href={viewAllUrl}
            aria-label="View all locations"
            className="group/tile relative block min-h-[160px] flex-shrink-0 overflow-hidden rounded-xl no-underline @[48rem]:min-h-0 @[48rem]:w-[36%]"
            style={{
              backgroundImage: `url("${imageUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div
              className="absolute inset-0 transition-colors group-hover/tile:bg-black/40"
              style={{ background: 'rgba(0,0,0,0.5)' }}
            />
            <div className="relative flex h-full flex-col justify-end p-6 text-white @[48rem]:p-8">
              <h2 className="font-display m-0 text-[48px] leading-none tracking-wide text-white @[72rem]:text-[64px]">
                Locations
              </h2>
              <ViewAllLink light asLabel className="mt-3 self-end" />
            </div>
          </a>
        )}
        {/* Side by side, the cards scroll inside the panel so a mega menu never
            runs off the bottom of the screen; the tile stretches to match. */}
        <div
          ref={columnRef}
          className="relative flex min-w-0 flex-1 flex-col"
          style={cap ? { maxHeight: cap } : undefined}
        >
          {/* While there is more below, the last row fades into the panel. A
              mask, not an overlay, so the faded cards still take clicks. */}
          <div
            ref={scrollRef}
            className="no-scrollbar min-h-0 flex-1 overflow-y-auto"
            style={more.hasMore && !more.atBottom ? SCROLL_FADE : undefined}
          >
            {errorLine}
            {cards}
          </div>
        </div>
      </div>
      {/* A footer row under both columns: the cue centred on the whole panel,
          "View all locations" bottom right (the photo tile says it already
          when there is no campus). Padded, the row is the bottom padding,
          as tall as the top, so the cards sit evenly between the two. The
          cue fades out at the end. */}
      {(padded || featured || more.hasMore) && (
        <div
          className={`relative flex items-center justify-end ${
            padded ? 'h-10' : 'mt-3 min-h-[28px]'
          }`}
        >
          {more.hasMore && (
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200"
              style={{ opacity: more.atBottom ? 0 : 1 }}
              aria-hidden={more.atBottom}
            >
              <KnockoutCue
                label="Scroll for more"
                hidden={more.atBottom}
                onClick={() => {
                  const el = scrollRef.current;
                  el?.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
                }}
              />
            </div>
          )}
          {featured && <ViewAllLink href={viewAllUrl} className="relative" />}
        </div>
      )}
    </div>
  );
}

function ViewAllLink({
  href,
  light = false,
  asLabel = false,
  className = '',
}: {
  href?: string;
  light?: boolean;
  /** Render as a plain label inside a parent link (the photo tile) */
  asLabel?: boolean;
  className?: string;
}) {
  const cls = `inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase no-underline ${className}`;
  const style = { color: light ? '#ffffff' : NAVY };
  const arrow = (
    <ArrowRight
      className="h-3.5 w-3.5 transition-transform group-hover/tile:translate-x-0.5"
      style={{ color: light ? '#ffffff' : TEAL }}
    />
  );
  if (asLabel) {
    return (
      <span className={cls} style={style}>
        View all locations
        {arrow}
      </span>
    );
  }
  return (
    <a
      href={href}
      className={`${cls} transition-transform hover:scale-105 active:scale-95`}
      style={style}
    >
      View all locations
      {arrow}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Card: one location, one or more congregations
// ---------------------------------------------------------------------------

/**
 * A card is a location. Each congregation in it is one link to its campus
 * page (art, name, pastor, service times, the whole block), with the heart
 * as the one control that sets your campus instead. No address or phone:
 * the campus page's hero has both, and the panel stays short enough to show
 * more campuses before it scrolls.
 */
function LocationCard({
  group,
  cardRef,
  selectedId,
  detailsBaseUrl,
  liveByCampus,
  onSelect,
}: {
  group: LocationGroup;
  cardRef: (el: HTMLElement | null) => void;
  selectedId: number | null;
  detailsBaseUrl: string;
  liveByCampus: Map<number, CampusLive>;
  onSelect: (cong: Congregation, svgUrl: string | null) => void;
}) {
  const primary = getPrimary(group);
  const subs = getSubCongregations(group);
  const hasSelected = group.congregations.some((c) => c.id === selectedId);

  // A live congregation whose block was clicked, waiting on "stream or page?"
  const [prompt, setPrompt] = useState<number | null>(null);
  const cardEl = useRef<HTMLDivElement | null>(null);
  const promptCong = prompt == null ? undefined : group.congregations.find((c) => c.id === prompt);
  const promptLive = prompt == null ? undefined : liveByCampus.get(prompt);
  const togglePrompt = (id: number) => setPrompt((cur) => (cur === id ? null : id));
  // Escape or a click anywhere else puts the question away
  useEffect(() => {
    if (prompt == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPrompt(null);
    };
    const onDown = (e: PointerEvent) => {
      const el = cardEl.current;
      if (el && !e.composedPath().includes(el)) setPrompt(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [prompt]);

  return (
    <div
      ref={(el) => {
        cardRef(el);
        cardEl.current = el;
      }}
      className={`@container relative flex flex-col overflow-hidden rounded-xl border bg-white pb-1 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        hasSelected ? 'border-transparent' : 'border-gray-200 shadow-sm'
      }`}
      style={
        hasSelected
          ? { boxShadow: `inset 0 0 0 2px ${TEAL}, 0 4px 12px rgba(0,0,0,0.08)` }
          : undefined
      }
    >
      <CongregationLink
        cong={primary}
        svgUrl={group.svg_url}
        isSelected={primary.id === selectedId}
        href={campusDetailsUrl(primary, detailsBaseUrl)}
        live={liveByCampus.get(primary.id)}
        onSelect={() => onSelect(primary, group.svg_url)}
        onLivePrompt={() => togglePrompt(primary.id)}
      />
      {subs.map((cong) => (
        <React.Fragment key={cong.id}>
          <div className="mx-3 border-t border-gray-100" />
          <CongregationLink
            cong={cong}
            svgUrl={null}
            isSelected={cong.id === selectedId}
            href={campusDetailsUrl(cong, detailsBaseUrl)}
            live={liveByCampus.get(cong.id)}
            onSelect={() => onSelect(cong, group.svg_url)}
            onLivePrompt={() => togglePrompt(cong.id)}
            nested
          />
        </React.Fragment>
      ))}

      {promptCong && promptLive?.isLive && (
        <LiveChoice
          cong={promptCong}
          watchUrl={promptLive.watchUrl}
          pageUrl={campusDetailsUrl(promptCong, detailsBaseUrl)}
          onClose={() => setPrompt(null)}
        />
      )}
    </div>
  );
}

function CongregationLink({
  cong,
  svgUrl,
  isSelected,
  href,
  onSelect,
  live,
  onLivePrompt,
  nested = false,
}: {
  cong: Congregation;
  /** The location's art, on the first congregation only */
  svgUrl: string | null;
  isSelected: boolean;
  href: string;
  onSelect: () => void;
  /** Livestream state for this congregation, when the API knows it */
  live?: CampusLive;
  /** Ask "stream or page?" instead of navigating, while the congregation is live */
  onLivePrompt?: () => void;
  /** A second congregation at the location, indented under the art column */
  nested?: boolean;
}) {
  return (
    <a
      href={href}
      aria-label={`${cong.name} campus`}
      onClick={(e) => {
        // A modifier click opens the campus page in a tab as usual. A plain
        // click on a campus that is streaming asks first (stream or page),
        // since the dot is small and the stream is what most people want on
        // a Sunday morning.
        if (!live?.isLive || !onLivePrompt) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onLivePrompt();
      }}
      className={`group flex flex-col gap-1.5 no-underline ${
        nested ? 'px-3 pt-2 pb-2' : 'px-3 pt-3 pb-2'
      }`}
      style={{ color: NAVY }}
    >
      {/* Name row: art (first congregation only), name, pastor */}
      <div className="flex items-center gap-2.5">
        {/* A second congregation keeps the art column empty so its name lines up under the first */}
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center" aria-hidden="true">
          {!nested &&
            (svgUrl ? (
              <img src={svgUrl} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <MapPin className="h-5 w-5 text-gray-400" />
            ))}
        </div>
        <p
          className={`font-display flex min-w-0 flex-1 items-center gap-2 leading-none tracking-wide transition-colors group-hover:text-[#299c8f] ${nested ? 'text-[20px]' : 'text-[24px]'}`}
        >
          <span className="min-w-0 truncate">{cong.display_name}</span>
          {live?.isLive && <LiveDot watchUrl={live.watchUrl} />}
        </p>
        {cong.pastor.name && <Pastor name={cong.pastor.name} imageUrl={cong.pastor.image_url} />}
      </div>

      {/* Service times, the heart, and a chevron that says the block is a link */}
      <div className="flex min-h-[28px] items-center justify-between gap-3">
        <ServiceTimes cong={cong} />
        <span className="-mr-1.5 flex flex-shrink-0 items-center">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isSelected) onSelect();
            }}
            aria-label={isSelected ? 'Your campus' : `Set ${cong.name} as my campus`}
            aria-pressed={isSelected}
            title={isSelected ? 'Your campus' : 'Set as my campus'}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-transparent p-0 transition-transform hover:scale-110 active:scale-95"
          >
            <Heart
              className="h-4 w-4"
              fill={isSelected ? TEAL : 'none'}
              style={{ color: isSelected ? TEAL : '#9ca3af' }}
            />
          </button>
          <span
            className="flex h-7 w-7 items-center justify-center transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          >
            <ChevronRight className="h-4 w-4" style={{ color: NAVY }} />
          </span>
        </span>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * The scroll cue: a teal glass pill with the label and chevrons cut out of
 * it, so the (blurred) cards show through the letters. The pill is an SVG
 * rect masked by its own text, drawn over a backdrop-blur wrapper; the text
 * is measured once fonts are ready so the pill hugs it.
 */
function KnockoutCue({
  label,
  hidden,
  onClick,
}: {
  label: string;
  hidden: boolean;
  onClick: () => void;
}) {
  const id = React.useId().replace(/:/g, '');
  const textRef = useRef<SVGTextElement>(null);
  const [textW, setTextW] = useState(96);
  useLayoutEffect(() => {
    const measure = () => {
      const w = textRef.current?.getComputedTextLength() ?? 0;
      if (w > 0) setTextW(Math.ceil(w));
    };
    measure();
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(measure).catch(() => {});
    }
  }, [label]);

  const H = 28;
  const PAD = 14;
  const CHEV = 14;
  const GAP = 6;
  const W = PAD * 2 + CHEV * 2 + GAP * 2 + textW;
  const chevY = (H - CHEV) / 2;
  const chevron = (x: number) => (
    <g className="cue-nudge">
      <path
        d="m6 9 6 6 6-6"
        transform={`translate(${x} ${chevY}) scale(${CHEV / 24})`}
        fill="none"
        stroke="#000"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );

  return (
    <button
      type="button"
      tabIndex={-1}
      onClick={onClick}
      aria-label={label}
      className={`block cursor-pointer overflow-hidden rounded-full border-0 bg-transparent p-0 shadow-[0_6px_20px_rgba(28,43,57,0.18)] ring-1 ring-white/50 backdrop-blur-xl backdrop-saturate-150 transition-all hover:scale-105 hover:brightness-110 active:scale-95 ${
        hidden ? 'pointer-events-none' : 'pointer-events-auto'
      }`}
      style={{ width: W, height: H }}
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block" aria-hidden="true">
        <defs>
          <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
            <rect width={W} height={H} fill="#fff" />
            {chevron(PAD)}
            <text
              ref={textRef}
              x={PAD + CHEV + GAP}
              y={H / 2}
              dominantBaseline="central"
              fill="#000"
              fontSize={10.5}
              fontWeight={700}
              letterSpacing="0.06em"
              style={{ fontFamily: 'inherit' }}
            >
              {label.toUpperCase()}
            </text>
            {chevron(W - PAD - CHEV)}
          </mask>
        </defs>
        <rect width={W} height={H} rx={H / 2} fill="rgba(41,156,143,0.82)" mask={`url(#${id})`} />
      </svg>
    </button>
  );
}

/**
 * A small pulsing red dot for a campus that is streaming right now. The
 * block around it is already a link to the campus page, so the dot is a
 * button that opens the stream instead.
 */
export function LiveDot({ watchUrl, className = '' }: { watchUrl: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(watchUrl, '_blank', 'noopener,noreferrer');
      }}
      aria-label="Live now. Watch the stream"
      title="Live now"
      className={`relative flex h-4 w-4 flex-shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 ${className}`}
    >
      <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-red-500 opacity-75 motion-safe:animate-ping" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-white" />
    </button>
  );
}

/**
 * What a click on a live campus asks: the stream, or the campus page. The
 * same words and buttons as the location finder's LiveChoice. It covers the
 * card as glass instead of growing it, so the grid holds still inside the
 * mega menu. Clicks inside stay inside.
 */
function LiveChoice({
  cong,
  watchUrl,
  pageUrl,
  onClose,
}: {
  cong: Congregation;
  watchUrl: string;
  pageUrl: string;
  onClose: () => void;
}) {
  // Focus lands on the stream link so Enter answers the question; React
  // only honours autoFocus on form controls, not anchors.
  const first = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    first.current?.focus({ preventScroll: true });
  }, []);
  return (
    <div
      role="group"
      aria-label={`${cong.display_name} is live now`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        e.stopPropagation();
      }}
      className="live-choice-in absolute inset-0 z-10 flex flex-col items-center justify-center gap-2.5 bg-white/80 p-3 text-center backdrop-blur-xl backdrop-saturate-150"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-1.5 right-1.5 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-400 transition-all hover:scale-110 hover:text-gray-600 active:scale-95"
      >
        <X className="h-4 w-4" />
      </button>
      <p className="m-0 text-[13px] leading-snug" style={{ color: NAVY }}>
        <span className="font-bold text-red-600">{cong.display_name} is live right now.</span> Where
        to?
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          ref={first}
          className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3.5 py-1.5 text-[11px] font-bold tracking-wider text-white uppercase no-underline shadow-sm transition-transform hover:scale-105 active:scale-95"
        >
          <Video className="h-3.5 w-3.5" strokeWidth={2.5} />
          Watch the stream
        </a>
        <a
          href={pageUrl}
          className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-[11px] font-bold tracking-wider text-white uppercase no-underline shadow-sm transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: NAVY }}
        >
          Campus page
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
        </a>
      </div>
    </div>
  );
}

/**
 * The campus pastor as an avatar alone; the name and title show on hover or
 * focus, to the left of the avatar so they stay inside the card.
 */
function Pastor({ name, imageUrl }: { name: string; imageUrl: string | null }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      tabIndex={0}
      role="img"
      aria-label={`Campus Pastor ${name}`}
      className="group/pastor relative flex flex-shrink-0 items-center outline-none"
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
          loading="lazy"
        />
      ) : (
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold text-white ring-2 ring-white"
          style={{ background: NAVY }}
        >
          {initials}
        </span>
      )}
      <span
        className="pointer-events-none absolute top-1/2 right-full mr-2 hidden -translate-y-1/2 flex-col items-end rounded-md px-2.5 py-1.5 text-right whitespace-nowrap text-white shadow-lg group-hover/pastor:flex group-focus-visible/pastor:flex"
        style={{ background: NAVY }}
      >
        <span className="text-[8.5px] leading-[11px] font-bold tracking-wider text-white/60 uppercase">
          Campus Pastor
        </span>
        <span className="text-[11px] leading-[14px] font-semibold">{name}</span>
      </span>
    </span>
  );
}

/**
 * The campus pastor with the "Campus Pastor" eyebrow and name beside the
 * avatar, as on the locations finder. Your campus's card has the room; the
 * grid cards keep the avatar alone. The name drops out when the card is too
 * narrow to hold it next to the campus name.
 */
function PastorLine({ name, imageUrl }: { name: string; imageUrl: string | null }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="flex flex-shrink-0 items-center gap-2.5" title={`Campus Pastor ${name}`}>
      <span className="hidden flex-col items-end text-right @[28rem]:flex">
        <span className="text-[9.5px] leading-[12px] font-bold tracking-wider text-gray-400 uppercase">
          Campus Pastor
        </span>
        <span className="text-[13px] leading-[16px] font-semibold" style={{ color: NAVY }}>
          {name}
        </span>
      </span>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-10 w-10 rounded-full object-cover ring-2 ring-white"
          loading="lazy"
        />
      ) : (
        <span
          className="flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-bold text-white ring-2 ring-white"
          style={{ background: NAVY }}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

/** The element whose overflow scrolls, walking up through shadow roots into the page */
function findScrollParent(start: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = start;
  while (el) {
    const next: HTMLElement | null =
      el.parentElement ??
      ((el.getRootNode() as ShadowRoot).host as HTMLElement | undefined) ??
      null;
    if (!next || next === document.body || next === document.documentElement) return null;
    const overflow = getComputedStyle(next).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return next;
    el = next;
  }
  return null;
}

/**
 * How tall the cards column may be so the whole panel fits its container.
 * In the site's mega menu the theme's own container has a max height and
 * scrolls; if the panel is taller, the theme scrolls everything, photo tile
 * included, and two scrollbars appear. So the column is sized to what is
 * left of that container after everything above and below it, and only the
 * column scrolls. With no scrolling container the viewport is the limit.
 * Null (no cap) when the tile is stacked above the cards, i.e. narrow.
 */
function useCardsColumnCap(
  rootRef: React.RefObject<HTMLDivElement | null>,
  columnRef: React.RefObject<HTMLDivElement | null>
): number | null {
  const [cap, setCap] = useState<number | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    const column = columnRef.current;
    if (!root || !column) return;

    function measure() {
      if (!root || !column) return;
      const rootRect = root.getBoundingClientRect();
      if (rootRect.height === 0 || rootRect.width < 768) {
        setCap(null);
        return;
      }
      const columnRect = column.getBoundingClientRect();
      const scroller = findScrollParent(root);
      // Work in the scroller's content coordinates so its current scroll
      // position and the column's current height both drop out.
      let contentTop: number;
      let visibleHeight: number;
      let contentBottom: number;
      if (scroller) {
        const sr = scroller.getBoundingClientRect();
        contentTop = sr.top - scroller.scrollTop;
        visibleHeight = scroller.clientHeight;
        contentBottom = contentTop + scroller.scrollHeight;
      } else {
        contentTop = 0;
        visibleHeight = window.innerHeight;
        contentBottom = Math.max(document.documentElement.scrollHeight, rootRect.bottom + 16);
      }
      const above = columnRect.top - contentTop;
      const below = contentBottom - columnRect.bottom;
      const next = Math.max(240, Math.floor(visibleHeight - above - below));
      setCap((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    }

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [rootRef, columnRef]);

  return cap;
}

/**
 * A fade at the bottom of the scrolling cards with "Scroll for more", shown
 * only while there is more below. macOS hides scrollbars until you scroll,
 * so a clipped row would otherwise be the only hint. Re-measured whenever
 * the column resizes, which is how it learns the mega menu just opened.
 */
/** Whether a scroll container overflows, and whether it is scrolled to the end. */
function useScrollMore(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [state, setState] = useState({ hasMore: false, atBottom: true });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function check() {
      if (!el) return;
      const hasMore = el.scrollHeight > el.clientHeight + 20;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      setState((prev) =>
        prev.hasMore === hasMore && prev.atBottom === atBottom ? prev : { hasMore, atBottom }
      );
    }
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [containerRef]);

  return state;
}

/**
 * One day's times on one line: "8:30 · 10:00 · 11:30AM". When every time
 * that day shares AM or PM it is said once, at the end.
 */
function dayTimes(services: ServiceTime[]): string {
  const parts = services.map((s) => {
    const m = s.time_of_day.match(/^(.*?)\s*([AP]M)$/i);
    return m ? { clock: m[1], half: m[2].toUpperCase() } : { clock: s.time_of_day, half: '' };
  });
  const halves = new Set(parts.map((p) => p.half));
  if (halves.size === 1) {
    const half = parts[0].half;
    return parts.map((p) => p.clock).join(' · ') + half;
  }
  return parts.map((p) => p.clock + p.half).join(' · ');
}

/** Times by day, one line per day: "SUN 8:30 · 10:00 · 11:30AM" */
function ServiceTimes({ cong, large = false }: { cong: Congregation; large?: boolean }) {
  const days = servicesByDay(cong);
  if (days.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-0.5">
      {days.map((d) => (
        <p
          key={d.day}
          className={`m-0 flex items-baseline gap-1.5 whitespace-nowrap ${
            large ? 'text-[14px] leading-5' : 'text-[12px] leading-snug'
          }`}
        >
          <span
            className={`font-bold tracking-wider text-gray-400 uppercase ${
              large ? 'text-[11px]' : 'text-[9.5px]'
            }`}
          >
            {d.day}
          </span>
          <span className="font-semibold" style={{ color: NAVY }}>
            {dayTimes(d.services)}
          </span>
        </p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Featured: your campus, with its announcements
// ---------------------------------------------------------------------------

/**
 * Your campus, bigger, in the photo's place: art, name, pastor, times, and
 * the first few announcements from its home page carousel. The whole card
 * goes to the campus page; the thumbnails go to the same page, which then
 * scrolls down to its announcements. At this size they are a preview of the page, not
 * something to aim at, so no thumbnail opens its own announcement.
 */
function FeaturedCampus({
  cong,
  svgUrl,
  href,
  live,
  apiBaseUrl,
}: {
  cong: Congregation;
  svgUrl: string | null;
  href: string;
  live?: CampusLive;
  apiBaseUrl: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const announcements = useAnnouncementsWhenVisible(ref, apiBaseUrl, cong.id);

  // Two links that fill the card between them: the campus, and its
  // announcements (the same page, scrolled to the announcements widget).
  return (
    <div
      ref={ref}
      className="group @container relative flex min-h-0 flex-1 flex-col rounded-xl bg-white transition-shadow hover:shadow-lg"
      style={{ boxShadow: `inset 0 0 0 2px ${TEAL}, 0 4px 12px rgba(0,0,0,0.08)` }}
    >
      <a
        href={href}
        aria-label={`${cong.name} campus page`}
        className="flex flex-1 flex-col gap-3 p-4 pb-3 no-underline @[72rem]:p-5 @[72rem]:pb-3"
        style={{ color: NAVY }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center"
            aria-hidden="true"
          >
            {svgUrl ? (
              <img src={svgUrl} alt="" className="h-12 w-12 object-contain" />
            ) : (
              <MapPin className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="m-0 flex items-center gap-1 text-[10px] leading-none font-bold tracking-wider uppercase"
              style={{ color: TEAL }}
            >
              <Heart className="h-2.5 w-2.5" fill={TEAL} style={{ color: TEAL }} />
              Your campus
            </p>
            <p className="font-display m-0 mt-1 flex min-w-0 items-center gap-2 text-[32px] leading-none tracking-wide transition-colors group-hover:text-[#299c8f] @[72rem]:text-[38px]">
              <span className="min-w-0 truncate">{cong.display_name}</span>
              {live?.isLive && <LiveDot watchUrl={live.watchUrl} />}
              <ChevronRight
                className="h-6 w-6 flex-shrink-0 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </p>
          </div>
          {cong.pastor.name && (
            <PastorLine name={cong.pastor.name} imageUrl={cong.pastor.image_url} />
          )}
        </div>
        <ServiceTimes cong={cong} large />
      </a>

      {announcements !== null && announcements.items.length === 0 ? null : (
        <a
          href={`${href}#${ANNOUNCEMENTS_HASH}`}
          aria-label={`${cong.name} announcements`}
          className="group/thumbs grid grid-cols-3 gap-2 px-4 pb-4 no-underline @[72rem]:px-5 @[72rem]:pb-5"
        >
          {announcements === null
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="aspect-video animate-pulse rounded-lg bg-gray-100" />
              ))
            : announcements.items.map((a, i, items) => (
                <AnnouncementThumb
                  key={a.id}
                  announcement={a}
                  // The last preview carries the count of the ones not shown
                  // here, itself included: "+5" means five more on the page.
                  more={
                    i === items.length - 1 && announcements.total > items.length
                      ? announcements.total - items.length + 1
                      : 0
                  }
                />
              ))}
        </a>
      )}
    </div>
  );
}

/** One carousel image, or the carousel's own navy title tile when there is none */
function AnnouncementThumb({
  announcement,
  more = 0,
}: {
  announcement: CampusAnnouncement;
  /** Covers the image with "+N" when there are N more on the campus page */
  more?: number;
}) {
  return (
    <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-100">
      {announcement.image_url ? (
        <img
          src={announcement.image_url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover/thumbs:scale-[1.03]"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center p-2"
          style={{ backgroundColor: NAVY }}
        >
          <span className="line-clamp-2 text-center text-[10px] leading-tight font-extrabold tracking-tight text-white uppercase">
            {announcement.title}
          </span>
        </div>
      )}
      {more > 0 && (
        <div
          className="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] transition-colors"
          style={{ backgroundColor: 'color-mix(in srgb, #1c2b39 62%, transparent)' }}
        >
          <span className="font-display text-[28px] leading-none tracking-wide text-white transition-transform group-hover/thumbs:scale-110">
            +{more}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * A campus's announcements, fetched the first time the card is on screen
 * (the mega menu mounts hidden on every page). Null while loading.
 */
function useAnnouncementsWhenVisible(
  ref: React.RefObject<HTMLElement | null>,
  apiBaseUrl: string,
  congregationId: number
): CampusAnnouncements | null {
  const [state, setState] = useState<{ id: number; data: CampusAnnouncements } | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    fetchCampusAnnouncements(apiBaseUrl, congregationId).then((data) => {
      if (!cancelled) setState({ id: congregationId, data });
    });
    return () => {
      cancelled = true;
    };
  }, [visible, apiBaseUrl, congregationId]);

  return state && state.id === congregationId ? state.data : null;
}
